import { VideoTypes } from './types.js';

const onYtReady = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    // The mount div normally lives in the component's shadow root, but the
    // lightbox physically re-parents the player's .video-container into its
    // overlay (document scope) when opened — look there as a fallback. Shadow
    // trees are excluded from document.getElementById, so this only matches the
    // re-parented node.
    const playerElement = ((_a = ctx.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(`player_${ctx.videoId}`)) ||
        document.getElementById(`player_${ctx.videoId}`);
    if (!playerElement)
        return;
    const playerVars = {
        rel: '0',
        origin: window.location.origin,
        enablejsapi: '1',
    };
    // When the player is created in response to a play gesture (not a
    // consent-at-load init), bake autoplay into the embed URL so playback starts
    // on load rather than via a post-ready playVideo() call, which the browser's
    // autoplay policy is more likely to block. Never emit autoplay=0.
    if (ctx.playWhenReady)
        playerVars.autoplay = 1;
    ctx.player = new window.YT.Player(playerElement, {
        host: 'https://www.youtube-nocookie.com',
        videoId: ctx.videoId,
        playerVars,
        events: {
            onReady: ctx.onYouTubePlayerReady,
            onStateChange: ctx.onYouTubePlayerStateChange,
        },
    });
};
const loadYoutubeIframeAPIScript = () => {
    var _a;
    if (!window.YT && !window.isYTInitializing) {
        window.isYTInitializing = true;
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        (_a = firstScriptTag.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(tag, firstScriptTag);
    }
};
// DCCC-8359: loading the IFrame API script from www.youtube.com sets tracking
// cookies, so it must not happen on page load — only after the OneTrust group
// covering YouTube is consented to, or on an explicit user gesture (click-to-load).
const YOUTUBE_CONSENT_GROUP = 'C0004';
const hasYoutubeConsent = () => typeof window.OnetrustActiveGroups === 'string' &&
    window.OnetrustActiveGroups.includes(YOUTUBE_CONSENT_GROUP);
const youtubeConsentCallbacks = [];
let youtubeConsentListenerAttached = false;
const drainYoutubeConsentCallbacks = () => {
    if (!hasYoutubeConsent())
        return;
    while (youtubeConsentCallbacks.length) {
        youtubeConsentCallbacks.shift()();
    }
};
const whenYoutubeConsent = (callback) => {
    if (hasYoutubeConsent()) {
        callback();
        return;
    }
    youtubeConsentCallbacks.push(callback);
    if (!youtubeConsentListenerAttached) {
        youtubeConsentListenerAttached = true;
        window.addEventListener('OneTrustGroupsUpdated', drainYoutubeConsentCallbacks);
        document.addEventListener('OneTrustGroupsUpdated', drainYoutubeConsentCallbacks);
    }
};
const requestYoutubePlayerInit = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    if ((_a = window.YT) === null || _a === void 0 ? void 0 : _a.Player) {
        onYtReady(ctx);
        return;
    }
    if (!window.videoPlayerReadyCallbacks)
        window.videoPlayerReadyCallbacks = [];
    if (!window.videoPlayerReadyCallbacks.some(cb => cb.context === ctx)) {
        window.videoPlayerReadyCallbacks.push({
            func: onYtReady,
            context: ctx,
        });
    }
    if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
            window.videoPlayerReadyCallbacks.forEach(cb => {
                cb.func(cb.context);
            });
            window.videoPlayerReadyCallbacks.length = 0;
        };
    }
    loadYoutubeIframeAPIScript();
};
// Re-issue playVideo until the player actually starts. Opening a lightbox
// re-parents (and reloads) the player iframe, so a single play command can
// land mid-reload and be dropped; poll the state briefly until it plays.
const ensurePlaying = (ctx, attempt = 0) => {
    var _a, _b, _c, _d;
    const state = (_b = (_a = ctx.player) === null || _a === void 0 ? void 0 : _a.getPlayerState) === null || _b === void 0 ? void 0 : _b.call(_a);
    if (state === 1 || state === 3)
        return; // playing / buffering
    (_d = (_c = ctx.player) === null || _c === void 0 ? void 0 : _c.playVideo) === null || _d === void 0 ? void 0 : _d.call(_c);
    if (attempt < 8) {
        setTimeout(() => ensurePlaying(ctx, attempt + 1), 250);
    }
};
// Create the nocookie embed iframe synchronously inside the user's click so
// the browser carries the gesture into the iframe load and permits autoplay.
// Waiting for the IFrame API to create it (a network round-trip later) makes
// the load non-gesture-initiated and autoplay gets blocked; instead the API
// attaches to this existing iframe (enablejsapi + origin) if consent allows.
const mountCookieFreeIframe = (ctx, withAutoplay = true) => {
    var _a;
    const id = `player_${ctx.videoId}`;
    const mount = ((_a = ctx.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(id)) || document.getElementById(id);
    if (!mount || mount.tagName === 'IFRAME')
        return;
    const iframe = document.createElement('iframe');
    iframe.id = id;
    iframe.title = ctx.videoname || 'youtube player';
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('allowfullscreen', '');
    iframe.src = `https://www.youtube-nocookie.com/embed/${ctx.videoId}?rel=0&playsinline=1${withAutoplay ? '&autoplay=1' : ''}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
    mount.replaceWith(iframe);
};
// Called from the players' onReady handlers: when the player was created in
// response to a play gesture, start playback exactly once.
const playIfRequested = (ctx) => {
    if (!ctx.playWhenReady)
        return;
    ctx.playWhenReady = false;
    ensurePlaying(ctx);
};
const initYoutubePlayer = (ctx) => {
    if (ctx.type !== VideoTypes.YOUTUBE)
        return;
    requestYoutubePlayerInit(ctx);
};
const initAndPlayYoutube = (ctx) => {
    var _a;
    if (ctx.type !== VideoTypes.YOUTUBE)
        return false;
    if ((_a = ctx.player) === null || _a === void 0 ? void 0 : _a.playVideo) {
        ensurePlaying(ctx);
        return true;
    }
    mountCookieFreeIframe(ctx, true);
    if (hasYoutubeConsent()) {
        ctx.playWhenReady = true;
        initYoutubePlayer(ctx);
    }
    else {
        // Playback needs no YouTube API — the nocookie embed plays cookie-free.
        // The API bootstrap (whose response sets .youtube.com cookies) attaches
        // only if/when consent is granted, and playWhenReady stays false so the
        // late attach never (re)starts playback.
        whenYoutubeConsent(() => initYoutubePlayer(ctx));
    }
    return true;
};
// ---------------------------------------------------------------------------
// DCCC-8445: Vimeo. The player.vimeo.com embed sets tracking cookies (vuid)
// on load unless the documented Do-Not-Track param is present, so pre-consent
// embeds always carry dnt=1 (Vimeo: "no session cookies or analytics").
// The OneTrust group C0004 covers third-party video embeds generally, which
// is why Vimeo reuses the same consent gate as YouTube.
const vimeoEmbedSrc = (videoId, params = '') => {
    const base = `https://player.vimeo.com/video/${videoId}${params}`;
    if (hasYoutubeConsent())
        return base;
    return `${base}${base.includes('?') ? '&' : '?'}dnt=1`;
};
// AC5: accepting all cookies must take effect on the current page view. An
// idle embed (rendered with dnt=1, never gesture-played) is reloaded without
// dnt so Vimeo's cookies get set; a gesture-initiated embed (autoplay=1) may
// be mid-playback and reloading would restart it, so it is left untouched —
// its cookies arrive with the next render instead.
const upgradeVimeoDntOnConsent = (ctx) => {
    if (ctx.type !== VideoTypes.VIMEO)
        return;
    whenYoutubeConsent(() => {
        var _a;
        // The players bind src as a property (lit `.src=`), so read the property —
        // the src attribute is never set on these iframes.
        const iframe = (_a = ctx.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('iframe');
        if (!(iframe === null || iframe === void 0 ? void 0 : iframe.src))
            return;
        const url = new URL(iframe.src);
        if (!url.searchParams.has('dnt') || url.searchParams.has('autoplay'))
            return;
        url.searchParams.delete('dnt');
        iframe.src = url.toString();
    });
};

export { YOUTUBE_CONSENT_GROUP, ensurePlaying, hasYoutubeConsent, initAndPlayYoutube, initYoutubePlayer, loadYoutubeIframeAPIScript, mountCookieFreeIframe, onYtReady, playIfRequested, requestYoutubePlayerInit, upgradeVimeoDntOnConsent, vimeoEmbedSrc, whenYoutubeConsent };
